import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Download, RefreshCw, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useLocation } from "react-router-dom";

export const GlobalFloatingButtons = () => {
  const [isInstalled, setIsInstalled] = useState(false);
  const [supportPhone, setSupportPhone] = useState("5561998592965");
  const [supportMsg, setSupportMsg] = useState("Olá Hudy, preciso de ajuda com o aplicativo!");
  const location = useLocation();

  useEffect(() => {
    // Check if PWA is installed
    const checkInstalled = () => {
      const standalone = window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true;
      setIsInstalled(standalone);
    };
    
    checkInstalled();
    
    if (window.matchMedia) {
      const mediaQuery = window.matchMedia('(display-mode: standalone)');
      mediaQuery.addEventListener('change', checkInstalled);
      return () => mediaQuery.removeEventListener('change', checkInstalled);
    }
  }, []);

  useEffect(() => {
    const fetchBranding = async () => {
      const { data } = await supabase.from("site_settings" as any).select("*");
      if (data) {
        const phone = data.find((s: any) => s.key === "support_whatsapp")?.value;
        const msg = data.find((s: any) => s.key === "support_message")?.value;
        if (phone) setSupportPhone(phone);
        if (msg) setSupportMsg(msg);
      }
    };
    fetchBranding();
  }, []);

  const handleForceUpdate = async () => {
    toast.loading("Atualizando aplicativo...", { id: "update-toast" });
    
    // Clear caches
    if ('caches' in window) {
      try {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
      } catch (err) {
        console.error("Erro ao limpar cache", err);
      }
    }
    
    // Unregister service workers
    if ('serviceWorker' in navigator) {
      try {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (let registration of registrations) {
          await registration.unregister();
        }
      } catch (err) {
        console.error("Erro ao desregistrar SW", err);
      }
    }
    
    toast.success("App atualizado com sucesso!", { id: "update-toast" });
    
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  };

  const handleSupport = () => {
    window.open(`https://wa.me/${supportPhone}?text=${encodeURIComponent(supportMsg)}`, "_blank");
  };

  // Avoid covering specific bottom elements in certain pages if necessary
  // Adjust bottom offset depending on route
  let bottomOffset = "bottom-20 md:bottom-8";
  if (location.pathname.includes("/albums")) {
    bottomOffset = "bottom-[140px] md:bottom-8";
  } else if (location.pathname.includes("/s/") || location.pathname.includes("/public/album/")) {
    bottomOffset = "bottom-[100px] md:bottom-8";
  }

  return (
    <div className={`fixed ${bottomOffset} right-6 z-[999] flex flex-col gap-3 pointer-events-auto`}>
      {/* Download App Button - Visible Only If Not Installed */}
      {!isInstalled && (
        <motion.button
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => {
            localStorage.removeItem("pwa-install-dismissed");
window.dispatchEvent(new Event("force-pwa-prompt"));
          }}
          className="w-12 h-12 md:w-14 md:h-14 bg-gradient-primary rounded-2xl flex items-center justify-center text-foreground shadow-2xl shadow-primary/30 border border-foreground/10 animate-bounce"
          title="Instalar App"
        >
          <Download className="w-6 h-6" />
        </motion.button>
      )}

      {/* Force Update Button - Always Visible */}
      <motion.button
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={handleForceUpdate}
        className="w-12 h-12 md:w-14 md:h-14 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-2xl flex items-center justify-center text-white shadow-2xl shadow-blue-500/30 border border-white/10"
        title="Forçar Atualização do App"
      >
        <RefreshCw className="w-6 h-6" />
      </motion.button>

      {/* Support Button - Always Visible */}
      <motion.button
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={handleSupport}
        className="w-12 h-12 md:w-14 md:h-14 bg-[#25D366] rounded-2xl flex items-center justify-center text-white shadow-2xl shadow-green-500/30 border border-white/10 group"
        title="Suporte WhatsApp"
      >
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7 md:w-8 md:h-8 group-hover:scale-110 transition-transform">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/>
        </svg>
      </motion.button>
    </div>
  );
};
